import { useMemo } from "react";
import { History } from "lucide-react";
import { ProductCard } from "@/components/catalog/ProductCard";

export const RECENT_SEARCHES_KEY = "bb_recent_searches";
export const RECENTLY_VIEWED_KEY = "bb_recently_viewed";

export function readRecentList(key) {
  try {
    const value = JSON.parse(window.localStorage.getItem(key) || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

export function pushRecentSearch(term) {
  const cleaned = term.trim().toLowerCase();
  if (!cleaned) return;
  const list = [cleaned, ...readRecentList(RECENT_SEARCHES_KEY).filter((item) => item !== cleaned)].slice(0, 8);
  window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(list));
}

export function pushRecentlyViewed(productId) {
  const list = [productId, ...readRecentList(RECENTLY_VIEWED_KEY).filter((item) => item !== productId)].slice(0, 10);
  window.localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(list));
}

export function RecentSearches({ products, onSelect, tick }) {
  const items = useMemo(() => {
    const viewed = readRecentList(RECENTLY_VIEWED_KEY);
    const searches = readRecentList(RECENT_SEARCHES_KEY);
    const seen = new Set();
    const results = [];
    for (const id of viewed) {
      const product = products.find((item) => item.id === id);
      if (product && !seen.has(product.id)) {
        seen.add(product.id);
        results.push(product);
      }
    }
    for (const term of searches) {
      const query = term.toLowerCase();
      for (const product of products) {
        if (seen.has(product.id)) continue;
        const haystack = [product.name, product.description, product.category, ...(product.colors || [])].filter(Boolean).join(" ").toLowerCase();
        if (haystack.includes(query)) {
          seen.add(product.id);
          results.push(product);
        }
      }
    }
    return results.slice(0, 10);
  }, [products, tick]);

  if (items.length === 0) return null;

  return (
    <section className="pt-6" data-testid="recent-searches-section">
      <div className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] text-blue-700">
        <History className="h-4 w-4" /> Based on your recent searches
      </div>
      <div className="flex snap-x gap-5 overflow-x-auto pb-4" data-testid="recent-searches-row">
        {items.map((product, index) => (
          <div key={product.id} className="recent-card w-64 shrink-0 snap-start" style={{ animationDelay: `${index * 80}ms` }} data-testid={`recent-product-card-${product.id}`}>
            <ProductCard product={product} onSelect={onSelect} />
          </div>
        ))}
      </div>
    </section>
  );
}
